<script setup>
import { computed, ref } from 'vue';
import { vOnClickOutside } from '@vueuse/components';

const props = defineProps({
  modelValue: { type: [Number, String], default: '' },
  templates: { type: Array, default: () => [] },
  placeholder: { type: String, default: '' },
  emptyLabel: { type: String, default: '' },
  clearLabel: { type: String, default: '' },
  loadingLabel: { type: String, default: '' },
  error: { type: String, default: '' },
  isLoading: { type: Boolean, default: false },
  ariaLabel: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const isOpen = ref(false);

const selectedTemplate = computed(() =>
  props.templates.find(
    template => String(template.id) === String(props.modelValue)
  )
);

const triggerLabel = computed(() => {
  if (props.isLoading) return props.loadingLabel;
  return selectedTemplate.value?.name || props.placeholder;
});

const selectTemplate = id => {
  emit('update:modelValue', id);
  isOpen.value = false;
};
</script>

<template>
  <div v-on-click-outside="() => (isOpen = false)" class="relative min-w-0">
    <button
      type="button"
      data-testid="saved-template-selector"
      class="flex h-10 w-full items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-left text-sm text-n-slate-12 outline-none hover:border-n-slate-6 focus:border-n-brand disabled:cursor-not-allowed disabled:opacity-50"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      :aria-label="ariaLabel || placeholder"
      :disabled="isLoading || !!error"
      @click="isOpen = !isOpen"
      @keydown.esc="isOpen = false"
    >
      <span class="min-w-0 truncate">{{ triggerLabel }}</span>
      <span
        class="size-4 shrink-0"
        :class="isOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      />
    </button>

    <div
      v-if="isOpen"
      role="listbox"
      :aria-label="ariaLabel || placeholder"
      class="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-2 p-1 shadow-xl"
    >
      <button
        v-if="modelValue"
        type="button"
        role="option"
        aria-selected="false"
        class="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2"
        @click="selectTemplate('')"
      >
        <span class="i-lucide-x size-4" />
        {{ clearLabel }}
      </button>
      <button
        v-for="template in templates"
        :key="template.id"
        type="button"
        role="option"
        :aria-selected="String(template.id) === String(modelValue)"
        data-testid="saved-template-option"
        class="flex w-full items-start justify-between gap-3 rounded-md px-3 py-2 text-left hover:bg-n-alpha-2"
        @click="selectTemplate(template.id)"
      >
        <span class="min-w-0">
          <span class="block truncate text-sm font-medium text-n-slate-12">
            {{ template.name }}
          </span>
          <span class="block text-xs text-n-slate-10">
            {{
              template.sender_name || template.subject || template.template_type
            }}
          </span>
        </span>
        <span
          v-if="String(template.id) === String(modelValue)"
          class="i-lucide-check size-4 shrink-0 text-n-brand"
        />
      </button>
      <p
        v-if="templates.length === 0"
        class="px-3 py-5 text-center text-sm text-n-slate-10"
      >
        {{ emptyLabel }}
      </p>
    </div>

    <p v-if="error" class="mt-1 text-xs text-n-ruby-10">
      {{ error }}
    </p>
  </div>
</template>
